import { Component, OnInit, OnDestroy } from '@angular/core';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { AssignedProfilesRepoService } from '@client/app/services/api/page-performance/assigned-profile-repo/assigned-profiles-repo.service';
import { PagePerformanceRescanRepoService } from '@client/app/services/api/page-performance/page-performance-rescan-repo/page-performance-rescan-repo.service';
import { PagePerformanceScanService } from '@client/app/modules/page-performance/services/page-performance-scan/page-performance-scan.service';
import { MonModalService } from '@client/app/modules/modals/services/mon-modal/mon-modal.service';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { PagePerformanceResultsRepoService } from '@client/app/services/api/page-performance/page-performance-results-repo/page-performance-results-repo.service';
import { PerformancePagesService } from '@client/app/modules/page-performance/pages/domain/pages/pages.service';
import { SessionService } from '@monsido/core/session/session.service';
import { AssignProfileService } from '@client/app/modules/page-performance/services/assign-profile/assign-profile.service';
import { AssignedProfilesObsService } from '@client/app/modules/page-performance/services/assigned-profiles-obs/assigned-profiles-obs.service';
import { ActiveFeatureService } from '@monsido/services/active-feature/active-feature.service';
import { PagePerformanceVersionService } from '@client/app/modules/page-performance/services/page-performance-version/page-performance-version.service';
import { interval } from 'rxjs';
import { StateService } from '@uirouter/core';
import {
    FormPagePerformancePageComponent,
} from '@client/app/modules/page-performance/forms/page/form-page-performance-page.component';
import { MON_EVENTS } from '@monsido/core/constants/mon-events.constant';
import { PageDetailsComponent } from '@client/app/components/pagedetails/details/page-details.component';
import { BaseContainerComponentController } from '@client/app/blocks/base/base.controller';

@Component({
    selector: 'page-performance-pages',
    standalone: false,
    template: `
<div class="page-performance-pages" *ngIf="!loadingInProgress || search.length">
    @if (assignedProfiles.length || search.length) {
    <div class="ml-4">
        <page-header header="<div class='pull-left'>{{'Performance Scans' | translate}}</div><div class='page-performance-header-label powered-by text-grey' translate>Powered by Google Lighthouse</div>"
                    [icon]="(['PAGE_PERFORMANCE', 'PERFORMANCE', 'ICON'] | monIcons)"
                    [subHeader]="('Overview of the measured performance scores per page. Add a new page from the button on the right.' | translate)">
            <page-header-content>
                <div class="col-48 text-right">
                    <button type="button"
                            class="btn btn-border-radius btn-secondary mr-1"
                            (click)="openPageForm()"
                            *ngIf="(['customer_admin'] | monRole)"
                            translate>
                        Add new page
                    </button>
                </div>
            </page-header-content>
        </page-header>
    </div>
    }
    @if (assignedProfiles.total || search.length) {
    <div class="row mb-3 ml-1 mr-1">
        <div class="col-48 offset-md-38 col-md-10">
            <table-search
                [minSearchLength]="3"
                class="block"
                [placeholderTxt]="('Search...' | translate)"
                [(model)]="search"
                (getPage)="onSearch($event)">
            </table-search>
        </div>
    </div>
    }
    <table-container [collection]="assignedProfiles"
                     *ngIf="assignedProfiles.total || search.length"
                     (onPageChange)="onPageChange($event)"
                     (onPerPageChange)="onPageSizeChange($event)"
                     class="pages-table-wrapper block mb-4">
        <div><div class="card panel-monsido-card" *ngFor="let assignedProfile of assignedProfiles">
            <div class="p-2 card-header">
                <div class="row align-items-center">
                    <div class="col-auto pr-sm-0">
                        <img [alt]="('Latest screenshot' | translate)"
                             [attr.src]="assignedProfile.screenshot"
                             *ngIf="assignedProfile.screenshot"
                             class="screenshot-box"/>
                        <div class="screenshot-box bg-light-grey"
                             aria-hidden="true"
                             *ngIf="!assignedProfile.screenshot">&nbsp;
                        </div>
                    </div>
                    <div class="col">
                        <div class="text-ellipsis">
                            <mon-url-title
                                    *ngIf="!pageDetailsIsAvailable"
                                    [title]="assignedProfile.page.title"
                                    [url]="assignedProfile.page.url"
                                    [active]="false">
                            </mon-url-title>
                            <url-title-page-details [target]="'page-details-section-page-performance'"
                                                    *ngIf="pageDetailsIsAvailable"
                                                    [subTarget]="subTarget"
                                                    [page]="assignedProfile.page"
                                                    [title]="assignedProfile.page.title"
                                                    [url]="assignedProfile.page.url"
                                                    (onClose)="callBack()"
                                                    [active]="true">
                            </url-title-page-details>
                        </div>
                    </div>
                    <div class="col-auto d-flex align-items-center">
                        <div *ngIf="hasProfile(assignedProfile) && hasDefaultProfile(assignedProfile)"
                             [hidden]="uncollapedAssignedProfile?.id === assignedProfile.id"
                             class="d-flex align-items-center">
                            <span class="text-bold mr-2 d-none d-md-block" translate>Performance score</span>
                            <table-score-chart
                                    [performanceScore]="assignedProfile.scores?.performance"
                                    class="pr-2 mt-1"
                                    [attr.aria-label]="('Performance score' | translate)"
                                    [triggers]="'focus hover'"
                                    [ngbTooltip]="('Performance score' | translate)">
                            </table-score-chart>
                        </div>
                        <div *ngIf="!hasProfile(assignedProfile)">
                            <span class="fas fa-exclamation-triangle fa-2x fa-fw text-orange mr-3"
                                  [ngbTooltip]="('No profile assigned. Please choose a new profile.' | translate)"
                                  [attr.aria-label]="('No profile assigned. Please choose a new profile.' | translate)">
                            </span>
                        </div>
                        <div *ngIf="hasProfile(assignedProfile) && !hasDefaultProfile(assignedProfile)">
                            <span class="fas fa-exclamation-triangle fa-2x fa-fw text-orange mr-3"
                                  [ngbTooltip]="('No default profile assigned. Please choose a new default profile.' | translate)"
                                  [attr.aria-label]="('No default profile assigned. Please choose a new default profile.' | translate)">
                            </span>
                        </div>
                        <table-page-details [ngbTooltip]="('Open Page Details' | translate)"
                                            [triggers]="'focus hover'"
                                            [target]="'page-details-section-page-performance'"
                                            [page]="assignedProfile.page"
                                            [ref]="ref"
                                            (callBack)="callBack()"
                                            class="mx-3">
                        </table-page-details>
                        <mon-action-menu [monActionMenuItems]="dropdownActionOptions[assignedProfile.id]">
                            <span translate>Actions</span>
                        </mon-action-menu>
                        <div>
                            <button type="button"
                                    [ngbTooltip]="('Expand' | translate)"
                                    (click)="setUncollapsedAssignedProfile(assignedProfile)"
                                    class="btn btn-border-radius btn-link"
                                    [hidden]="uncollapedAssignedProfile?.id === assignedProfile.id">
                                <span class="fad fa-expand-alt fa-2x" aria-hidden="true">
                                </span>
                                <span class="sr-only" translate>Expand</span>
                            </button>
                            <button type="button"
                                    [ngbTooltip]="('Collapse' | translate)"
                                    (click)="setUncollapsedAssignedProfile(null)"
                                    class="btn btn-border-radius btn-link"
                                    [hidden]="uncollapedAssignedProfile?.id !== assignedProfile.id">
                                <span class="fad fa-compress-alt fa-2x" aria-hidden="true"></span>
                                <span class="sr-only" translate>Collapse</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div [ngbCollapse]="uncollapedAssignedProfile === null || uncollapedAssignedProfile?.id !== assignedProfile.id">
                <div class="card-body pt-0">
                    <mon-spinner [loading]="loadingScanResult">
                        <div class="row">
                            <div class="offset-2 offset-xl-4 col-46 col-xl-44">
                                <div class="ml-3">
                                    <h3 translate>Performance score</h3>
                                    <p class="text-secondary-4"
                                       translate
                                       [translate-args]="{performanceScannedAt: (scanResult?.scanned_at | monUserDate:'LLL')}">
                                        Last check: [[performanceScannedAt]]
                                    </p>
                                </div>
                                <div class="d-flex align-items-center">
                                    <div class="d-flex flex-wrap flex-sm-nowrap align-items-center">
                                        <page-performance-score-doughnut-chart [score]="assignedProfile.scores?.performance * 100">
                                        </page-performance-score-doughnut-chart>

                                        <page-performance-score-indicators class="mx-6">
                                        </page-performance-score-indicators>
                                    </div>
                                    <div class="flex-fill mb-md-4">
                                       <div class="row">
                                           <div class="offset-6 offset-xl-8 col d-flex flex-wrap flex-row justify-content-start">
                                                <div class="checkpoint-container d-flex my-2" *ngFor="let metric of metrics">
                                                    <span class="vertical-ruler-performance vertical-ruler-left" [ngClass]="metric.rulerClass"></span>
                                                    <notification [title]="metric.title"
                                                                  [digit]="metric.data"
                                                                  [unit]="metric.unit">
                                                    </notification>
                                                </div>
                                           </div>
                                       </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </mon-spinner>
                </div>
                <div class="card-footer measurement-settings-row">
                    <div class="measurement-settings-container d-flex align-items-center">
                        <div class="measurement-settings-item d-inline-block">
                            <span class="mr-1 text-bold" translate>Measurement Settings</span>
                            <span class="fas fa-question-circle" [ngbTooltip]="('This section describes the settings set for this Page' | translate)" aria-hidden="true"></span>
                        </div>
                        <div class="measurement-settings-item d-inline-block">
                            <span [ngClass]="(['PAGE_PERFORMANCE', 'PROFILE', 'DEVICE'] | monIcons)" class="mr-1" aria-hidden="true"></span>
                            <span class="mr-1" translate>Device:</span><span class="text-bold text-nowrap">{{ getProfileDeviceName(assignedProfile) }}</span>
                        </div>
                        <div class="measurement-settings-item d-inline-block">
                            <span class="fas fa-map-marker-alt mr-1" aria-hidden="true"></span>
                            <span class="mr-1" translate>Measure from:</span>
                            <div class="inline-block text-nowrap">
                                <mon-flag-icon [flag]="getProfileCountryCode(assignedProfile)">
                                </mon-flag-icon>
                                <span class="text-bold ml-1 text-nowrap">{{getProfileLocation(assignedProfile)}}</span>
                            </div>
                        </div>
                        <div class="measurement-settings-item d-inline-block">
                            <span class="fas fa-broadcast-tower mr-1" aria-hidden="true"></span>
                            <span class="mr-1" translate>Network speed:</span><span class="text-bold text-nowrap">{{ getProfileConnectionName(assignedProfile) }}</span>
                        </div>
                        <div class="measurement-settings-item d-inline-block">
                            <span class="fas fa-history mr-1" aria-hidden="true"></span>
                            <span class="mr-1" translate>Check frequency:</span><span class="text-bold text-nowrap">
                                {{ assignedProfile.scan_frequency | frequencyTranslation }}
                            </span>
                        </div>
                        <div class="align-self-auto ml-auto d-flex flex-wrap align-items-center justify-content-end">
                            <p class="text-danger pr-md-2 text-right"
                               *ngIf="!hasProfile(assignedProfile)"
                               translate>
                                No profiles selected for this page, scoring not available.
                            </p>
                            <p class="text-danger pr-md-2 text-right"
                               *ngIf="hasProfile(assignedProfile) && !hasDefaultProfile(assignedProfile)"
                               translate>
                               No default profile selected for this page, scoring not available.
                            </p>
                            <button class="btn btn-border-radius btn-secondary p-2"
                                    *ngIf="canRescan()"
                                    type="button"
                                    [triggers]="'focus hover'"
                                    [placement]="'left'"
                                    [ngbTooltip]="('The scan can take a few minutes to finalise, depending on the size of the page' | translate)"
                                    (click)="performanceRescanPage(assignedProfile)"
                                    [disabled]="!hasProfile(assignedProfile) || assignedProfileIsScanning(assignedProfile) || !hasDefaultProfile(assignedProfile)">
                                <span class="fas fa-sync"
                                      [ngClass]="{'fa-spin': assignedProfileIsScanning(assignedProfile)}"
                                      aria-hidden="true">
                                </span>
                                <span translate>Rescan</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div></div>
    </table-container>
    <section class="no-pages-wrapper text-center font-weight-300" *ngIf="!assignedProfiles.total && !search.length">
        <div>
            <img class="img-responsive mx-auto lighthouse-illustration" src="/images/performance/lighthouse.png" alt="">
            <h1 class="line-height-32 text-grey-1 font-weight-400 font-size-24 line-height-32 mt-6 mb-1" translate>Performance</h1>
            <div class="line-height-16 text-grey-1 mt-0 mb-4 text-bold" translate>Powered by Google Lighthouse</div>
            <p class="line-height-16 text-grey-1 mt-0 mb-4" translate>Add a new page to start tracking individual pages' performance. Get an overview of load times and opportunities to speed up your site.</p>
            <button type="button"
                    class="btn btn-border-radius btn-secondary"
                    (click)="openPageForm()"
                    *ngIf="(['customer_admin'] | monRole)"
                    translate>
                Add new page
            </button>
        </div>
    </section>
</div>
<div class="spinner-wrapper" *ngIf="loadingInProgress && !search.length">
    <mon-spinner [loading]="true" ></mon-spinner>
</div>
    `,
})
export class PagePerformancePagesComponent extends BaseContainerComponentController implements OnInit, OnDestroy {
    override search: any = '';
    scanStatus: any = {};
    uncollapedAssignedProfile: any = null;
    scanResult: any = null;
    loadingScanResult: any = false;
    progress: any = null;
    metrics: any = [];
    pageDetailsIsAvailable: any;
    assignedProfiles: any = [];
    dropdownActionOptions: any = [];
    subTarget: any;
    ref: any;
    loadingInProgress: any = false;
    promise: any;

    constructor (
        private ng2MonEventsService: MonEventService,
        private ng2AssignedProfileRepoService: AssignedProfilesRepoService,
        private ng2PagePerformanceRescanRepoService: PagePerformanceRescanRepoService,
        private ng2PagePerformanceScanService: PagePerformanceScanService,
        private ng2MonModalService: MonModalService,
        private gettextCatalog: TranslateService,
        private ng2PagePerformanceResultsRepoService: PagePerformanceResultsRepoService,
        private performancePagesService: PerformancePagesService,
        private stateService: StateService,
        private ng2SessionService: SessionService,
        private PagePerformanceAssginProfileService: AssignProfileService,
        private PagePerformanceAssignedProfileObsService: AssignedProfilesObsService,
        private ng2ActiveFeatureService: ActiveFeatureService,
        private ng2PagePerformanceVersionService: PagePerformanceVersionService,
    ) {
        super({ table: true });
    }

    ngOnInit (): void {
        this.search = '';
        this.scanStatus = {};
        this.uncollapedAssignedProfile = null;
        this.scanResult = null;
        this.loadingScanResult = false;
        this.progress = null;
        this.metrics = [];
        this.pageDetailsIsAvailable = this.ng2ActiveFeatureService.isFeatureActive('page_details');
        this.assignedProfiles = [];
        this.performancePagesService.assignedProfilesObs.subscribe(assignedProfiles => {
            this.assignedProfiles = assignedProfiles;
        });

        let chain = Promise.resolve();
        chain = chain.then(this.getAssignedProfiles());
        this.promise = chain.finally(() => {
            if (this.stateService.params.showPage) {
                const targetPageId = this.stateService.params.showPage;
                this.getAssignedProfilesByPage(targetPageId).then((res) => {
                    if (res && res.length > 0) {
                        this.getPageDetails(targetPageId).then(this.goToPageDetails);
                    } else {
                        this.ng2MonModalService
                            .alert(this.gettextCatalog.getString('The Requested Page is not a part of the Performance Module'))
                            .then(() => {}, () => {});
                    }
                }, () => {});
            }
        });
    }

    ngOnDestroy (): void {
        Object.keys(this.scanStatus).forEach((assignedProfileKey) => {
            const profileObject = this.scanStatus[assignedProfileKey];
            Object.keys(profileObject).forEach((profileKey) => {
                const scanStatus = profileObject[profileKey];
                if (scanStatus !== null && !scanStatus.isStopped) {
                    this.scanStatus[assignedProfileKey][profileKey].unsubscribe();
                    this.scanStatus[assignedProfileKey][profileKey] = null;
                }
            });
        });
    }

    override getPage (): void {
        this.progress = this.getAssignedProfiles();
    }

    callBack (): void {
        // Implementation needed
    }

    openPageForm (assignedProfile?: any): void {
        const assignedProfileCount = this.PagePerformanceAssignedProfileObsService.assignedProfilesCount;
        if (!assignedProfile && this.performancePagesService.getMaxPageCount() <= assignedProfileCount) {
            this.PagePerformanceAssginProfileService.getReachedMaximumPageMessageDialog();
        } else {
            const params = {
                body: FormPagePerformancePageComponent,
                size: 'md',
                data: {
                    assignedProfile: assignedProfile,
                },
            };
            const callback = (updatedAssignedProfile) => {
                if (typeof updatedAssignedProfile !== 'undefined') {
                    const defer = new Promise<void>(resolve => {

                        if (this.canRescan()) {
                            if (!assignedProfile) {
                                this.rescanAllProfilesInAssignedProfile(updatedAssignedProfile);
                            }
                            resolve();
                        } else {
                            this.ng2MonModalService
                                .alert(this.gettextCatalog.getString('The Scan will take effect within the next few hours'))
                                .then(() => {
                                    resolve();
                                }, () => {});
                        }
                    });
                    defer.then(() => {
                        this.getPage();
                    });
                }
            };
            this.ng2MonEventsService.run(MON_EVENTS.LOAD_DIALOG, { params, callback });
        }
    }

    performanceRescanPage (assignedProfile: any): void {
        const profiles = this.performancePagesService.getProfiles();
        const profileId = this.getDefaultProfileIdFromAssignedProfile(assignedProfile);
        const profile = profiles[profileId];
        if (!profile) {
            // Profile not found; nothing to rescan
            return;
        }

        this.ng2PagePerformanceScanService.performanceRescan(assignedProfile.page, profile).then(() => {
            this.observePagePerformanceScanStatus(assignedProfile, profile);
        }, () => {});
    }

    deleteAssignedProfile (assignedProfile: any): any {
        const scope = {
            title: assignedProfile.page.title,
            titleTag: '<span class=\'text-link\'>',
            titleTagEnd: '</span>',
            deleteTag: '<code>',
            deleteTagEnd: '</code>',
            newline: '<br/>',
        };
        const msg = this.gettextCatalog.getString(
            'You are about to remove [[titleTag]][[title]][[titleTagEnd]] <br> Please Type [[deleteTag]]Delete[[deleteTagEnd]] to confirm this action',
            scope,
        );
        return this.ng2MonModalService.prompt(msg, {}).then((input) => {
            if ('delete' !== input.toLowerCase()) {
                return;
            }
            const params = {
                profile_id: assignedProfile.profile_id,
                page_id: assignedProfile.page.id,
            };

            this.ng2AssignedProfileRepoService.destroy(assignedProfile, params).then(() => {
                this.getPage();
            }, () => {});
        }, () => {});
    }

    setUncollapsedAssignedProfile (assignedProfile: any): void {
        this.uncollapedAssignedProfile = assignedProfile;
        if (assignedProfile === null) {
            return;
        }
        this.loadingScanResult = true;
        this.getScanResult(assignedProfile)
            .then((scanResults) => {
                this.scanResult = scanResults[0];
                if (this.scanResult) {
                    this.metrics = (this.ng2PagePerformanceVersionService.getVersionInfo(this.scanResult.version)?.metrics || []).map(metric => {
                        return {
                            title: metric.label,
                            data: this.scanResult[metric.id],
                            unit: metric.unit,
                            rulerClass: metric.rulerClass,
                        };
                    });
                }
            }, () => {})
            .finally(() => {
                this.loadingScanResult = false;
            });
    }

    assignedProfileIsScanning (assignedProfile: any): any {
        return Boolean(
            this.scanStatus[assignedProfile.id] &&
            Object.values(this.scanStatus[assignedProfile.id]).some((observable) => {
                return observable !== null;
            }),
        );
    }

    hasProfile (profile: any): any {
        const profileDict = this.performancePagesService.getProfiles();
        return (
            profile.profile_ids &&
            profile.profile_ids.length &&
            profile.profile_ids.some((profileId) => {
                return profileDict[profileId];
            })
        );
    }

    hasDefaultProfile (assignedProfile: any): any {
        const defaultId = this.getDefaultProfileIdFromAssignedProfile(assignedProfile);
        return Boolean(this.performancePagesService.getProfiles()[defaultId]);
    }

    getProfileDeviceName (deviceName: any): any {
        return this.performancePagesService.getProfileDeviceName(deviceName);
    }

    getProfileLocation (assignedProfile: any): any {
        return this.performancePagesService.getProfileLocation(assignedProfile);
    }

    getProfileCountryCode (assignedProfile: any): any {
        return this.performancePagesService.getProfileCountryCode(assignedProfile);
    }

    getProfileConnectionName (assignedProfile: any): any {
        return this.performancePagesService.getProfileConnectionName(assignedProfile);
    }

    canRescan (): any {
        return this.performancePagesService.canRescan();
    }

    // PROTECTED

    observePagePerformanceScanStatus (assignedProfile: any, profile: any): void {
        if (assignedProfile.profile_ids.length === 0) {
            return;
        }

        if (!this.scanStatus[assignedProfile.id]) {
            this.scanStatus[assignedProfile.id] = {};
        }

        this.scanStatus[assignedProfile.id][profile.id] = interval(40000).subscribe(() => {
            const params = {
                page_id: assignedProfile.page.id,
                profile_id: profile.id,
            };

            this.ng2PagePerformanceRescanRepoService.get(params).then((data) => {
                if (data[0]) {
                    if (data[0].status === 'finished') {
                        if (
                            this.scanStatus[assignedProfile.id][profile.id] !== null &&
                            !this.scanStatus[assignedProfile.id][profile.id].isStopped
                        ) {
                            this.scanStatus[assignedProfile.id][profile.id].unsubscribe();
                            this.scanStatus[assignedProfile.id][profile.id] = null;
                        }

                        if (this.getDefaultProfileIdFromAssignedProfile(assignedProfile) === profile.id) {
                            this.getAssignedProfiles();
                        }
                    }
                }
                return data;
            }, () => {});
        });
    }

    getDefaultProfileIdFromAssignedProfile (assignedProfile: any): any {
        return assignedProfile.default_profile_id || assignedProfile.profile_ids[0];
    }

    getScanResult (assignedProfile: any): any {
        const params = {
            profile_id: this.getDefaultProfileIdFromAssignedProfile(assignedProfile),
            page_id: assignedProfile.page.id,
            page: 1,
            page_size: 1,
        };
        return this.ng2PagePerformanceResultsRepoService.getAll(params as any, null);
    }

    rescanAllProfilesInAssignedProfile (assignedProfile: any): void {
        let chain = Promise.resolve();
        const profiles = this.performancePagesService.getProfiles();
        assignedProfile.profile_ids.forEach((profileId: any) => {
            chain = chain.then(() => {
                const profile = profiles[profileId];
                if (!profile) {
                    return Promise.resolve();
                }
                return this.rescanAssignedProfile(assignedProfile.page.id, profile.id).then(() => {
                    this.observePagePerformanceScanStatus(assignedProfile, profile);
                }, () => {});
            }, () => {});
        });
    }

    rescanAssignedProfile (pageId: any, profileId: any): any {
        return this.PagePerformanceAssginProfileService.rescanProfile(pageId, profileId);
    }

    getAssignedProfiles (): any {
        const params = {
            page: this.page,
            page_size: this.pageSize,
            search: this.search,
            domain_id: this.ng2SessionService.getDomainId(),
        };

        this.loadingInProgress = true;

        return this.getAllAssignedProfiles(params)
            .then(async (assignedProfiles) => {
                const profiles = assignedProfiles || [];
                profiles.forEach(assignedProfile => {
                    const actions: any[] = [];
                    if (this.ng2SessionService.hasRole('customer_admin')) {
                        actions.push({
                            label: this.gettextCatalog.getString('Edit'),
                            action: () => this.openPageForm(assignedProfile),
                            leftIcon: 'faCog',
                        });
                    }
                    actions.push({
                        label: this.gettextCatalog.getString('Delete'),
                        action: () => this.deleteAssignedProfile(assignedProfile),
                        leftIcon: 'faTrashAlt',
                    });

                    this.dropdownActionOptions[assignedProfile.id] = actions;
                });

                this.performancePagesService.setAssignedProfiles(assignedProfiles);
                if (this.ng2ActiveFeatureService.isFeatureActive('admin_page_performance')) {
                    await this.PagePerformanceAssignedProfileObsService.reloadAssignedProfiles();
                }
                this.loadingInProgress = false;
            }, () => {})
            .catch(() => {
                this.loadingInProgress = false;
            });
    }

    getPageDetails (pageId: any): any {
        return this.pageService.get(pageId).then(
            (page) => {
                return page;
            },
            () => {
                return null;
            },
        );
    }

    getAssignedProfilesByPage (pageId: any): any {
        const params = {
            page_id: pageId,
            domain_id: this.ng2SessionService.getDomainId(),
        };
        return this.getAllAssignedProfiles(params).then(
            (assignedProfiles) => {
                return assignedProfiles;
            },
            () => {
                return null;
            },
        );
    }

    goToPageDetails (page: any): void {
        if (!page) {
            return;
        }
        const data = {
            page: page,
            target: 'page-details-section-page-performance',
        };

        const params = {
            body: PageDetailsComponent,
            size: 'page-details',
            classes: 'page-details-app',
            data: data,
        };

        this.ng2MonEventsService.run(MON_EVENTS.LOAD_DIALOG, { params });
    }

    getAllAssignedProfiles (params: any): any {
        return this.ng2AssignedProfileRepoService.getAll(params);
    }
}
